/**
 * Pipe OD / makeup loss: whole inches + optional sixteenth fraction (e.g. 4 and 1/2).
 */

export type InchFraction =
  | ''
  | '1/16'
  | '1/8'
  | '3/16'
  | '1/4'
  | '5/16'
  | '3/8'
  | '7/16'
  | '1/2'
  | '9/16'
  | '5/8'
  | '11/16'
  | '3/4'
  | '13/16'
  | '7/8'
  | '15/16';

export const INCH_FRACTION_SELECT_OPTIONS: { value: InchFraction; label: string }[] = [
  { value: '', label: '—' },
  { value: '1/16', label: '1/16' },
  { value: '1/8', label: '1/8' },
  { value: '3/16', label: '3/16' },
  { value: '1/4', label: '1/4' },
  { value: '5/16', label: '5/16' },
  { value: '3/8', label: '3/8' },
  { value: '7/16', label: '7/16' },
  { value: '1/2', label: '1/2' },
  { value: '9/16', label: '9/16' },
  { value: '5/8', label: '5/8' },
  { value: '11/16', label: '11/16' },
  { value: '3/4', label: '3/4' },
  { value: '13/16', label: '13/16' },
  { value: '7/8', label: '7/8' },
  { value: '15/16', label: '15/16' }
];

const FRAC_DECIMAL: Record<string, number> = {
  '1/16': 1 / 16,
  '1/8': 1 / 8,
  '3/16': 3 / 16,
  '1/4': 1 / 4,
  '5/16': 5 / 16,
  '3/8': 3 / 8,
  '7/16': 7 / 16,
  '1/2': 1 / 2,
  '9/16': 9 / 16,
  '5/8': 5 / 8,
  '11/16': 11 / 16,
  '3/4': 3 / 4,
  '13/16': 13 / 16,
  '7/8': 7 / 8,
  '15/16': 15 / 16
};

export function fractionToDecimal(frac: InchFraction): number {
  if (!frac) {
    return 0;
  }
  return FRAC_DECIMAL[frac] ?? 0;
}

/** Map decimal inches to whole + nearest sixteenth (for hydrating forms from API). */
export function decimalToWholeFraction(value: number): { whole: string; fraction: InchFraction } {
  if (!Number.isFinite(value)) {
    return { whole: '', fraction: '' };
  }
  const sign = value < 0 ? -1 : 1;
  const x = Math.abs(value);
  const wholeNum = Math.floor(x + 1e-9);
  const rem = x - wholeNum;
  if (rem < 1e-5) {
    return { whole: wholeNum === 0 ? '' : String(sign * wholeNum), fraction: '' };
  }
  let best: InchFraction = '';
  let bestErr = Infinity;
  for (const f of Object.keys(FRAC_DECIMAL) as InchFraction[]) {
    if (!f) {
      continue;
    }
    const v = FRAC_DECIMAL[f];
    const err = Math.abs(rem - v);
    if (err < bestErr) {
      bestErr = err;
      best = f;
    }
  }
  if (bestErr > 0.04) {
    return { whole: String(sign * Number(x.toFixed(4))), fraction: '' };
  }
  return {
    whole: wholeNum === 0 ? '' : String(sign * wholeNum),
    fraction: best
  };
}

/** True when whole inches and/or fraction are present and whole parses as a number when non-empty. */
export function inchesPartsFilled(whole: string | undefined, frac: InchFraction | undefined): boolean {
  const w = (whole ?? '').trim();
  const f = frac ?? '';
  if (w !== '' || f !== '') {
    return w === '' || !Number.isNaN(Number(w));
  }
  return false;
}

function isInchFraction(s: string): s is InchFraction {
  return s === '' || Object.prototype.hasOwnProperty.call(FRAC_DECIMAL, s);
}

/** Parse stored size string (e.g. <c>4 1/2"</c>, <c>4.5</c>, <c>13</c>) into UI fields. */
export function parseStoredInchesString(raw: string): { whole: string; fraction: InchFraction } {
  const s = raw
    .trim()
    .replace(/[""`]/g, '')
    .replace(/\s+in(ch(es)?)?\.?$/i, '')
    .trim();
  if (!s) {
    return { whole: '', fraction: '' };
  }
  const pair = /^(\d+)\s+(\d+\/\d+)$/.exec(s);
  if (pair) {
    const frac = pair[2];
    return {
      whole: pair[1],
      fraction: isInchFraction(frac) ? frac : ''
    };
  }
  const decOnly = /^-?\d+\.\d+$/.exec(s);
  if (decOnly) {
    return decimalToWholeFraction(Number(s));
  }
  const intOnly = /^-?\d+$/.exec(s);
  if (intOnly) {
    return { whole: s, fraction: '' };
  }
  return { whole: s, fraction: '' };
}

/** Persisted / API string for <c>sizeDiameter</c> (decimal inches total for makeup stays numeric on API). */
export function formatInchesStored(whole: string | undefined, frac: InchFraction | undefined): string {
  const w = (whole ?? '').trim();
  const f = frac ?? '';
  if (w === '' && f === '') {
    return '';
  }
  if (w === '' && f !== '') {
    return `${f}"`;
  }
  if (w !== '' && f === '') {
    return `${w}"`;
  }
  return `${w} ${f}"`;
}

export function inchesPartsToDecimal(whole: string | undefined, frac: InchFraction | undefined): number | undefined {
  const w = (whole ?? '').trim();
  const f = frac ?? '';
  if (w === '' && f === '') {
    return undefined;
  }
  const wn = w === '' ? 0 : Number(w);
  if (Number.isNaN(wn)) {
    return undefined;
  }
  return wn + fractionToDecimal(f);
}

export interface CasingInchHydrateTarget {
  plusMakeupThreadLoss?: number;
  sizeDiameter?: string;
  plusMakeupInchesWhole?: string;
  plusMakeupInchesFraction?: InchFraction;
  sizeInchesWhole?: string;
  sizeInchesFraction?: InchFraction;
}

/** Fills whole-inch + fraction fields from API decimals / stored strings. */
export function hydrateCasingInchUiFromStored(c: CasingInchHydrateTarget): void {
  const pm = c.plusMakeupThreadLoss;
  if (pm !== undefined && pm !== null && Number.isFinite(Number(pm))) {
    const { whole, fraction } = decimalToWholeFraction(Number(pm));
    c.plusMakeupInchesWhole = whole;
    c.plusMakeupInchesFraction = fraction;
  } else {
    c.plusMakeupInchesWhole = c.plusMakeupInchesWhole ?? '';
    c.plusMakeupInchesFraction = (c.plusMakeupInchesFraction ?? '') as InchFraction;
  }
  const sd = (c.sizeDiameter ?? '').trim();
  if (sd) {
    const p = parseStoredInchesString(sd);
    c.sizeInchesWhole = p.whole;
    c.sizeInchesFraction = p.fraction;
  } else {
    c.sizeInchesWhole = c.sizeInchesWhole ?? '';
    c.sizeInchesFraction = (c.sizeInchesFraction ?? '') as InchFraction;
  }
}
